import React, { useState, useContext, useEffect } from 'react';
import { toast } from 'react-toastify';
import { AppContext } from '../../../contexts/AppContext';
import useDeliveryService from '../../../app/services/hooks/useDeliveryService';
import useSocietyService from '../../../app/services/hooks/useSocietyService';
import useClientService from '../../../app/services/hooks/useClientService';
import useNavigationPage from '../../../hooks/useNavigationPage';
import useReactConfirmAlert from '../../../hooks/useReactConfirmAlert';
import useFetch from '../../../hooks/useFetch';
import { DeliveryDataForm, defaultDeliveryDataForm } from '../../../app/models/Delivery';
import { Location } from '../../../app/models/Location';
import { Society } from '../../../app/models/Society';
import { Client } from '../../../app/models/Client';
import { ServiceResponse } from '../../../app/services/shared/interfaces';
import { EndPoints } from '../../../app/services/EndPoints';

import DeliveryForm from './forms/DeliveryForm';
import ButtonCancelForm from '../../../components/buttons/ButtonCancelForm';
import ButtonSaveForm from '../../../components/buttons/ButtonSaveForm';

interface Props {
    deliveryId: number;
    isDelivered?: boolean;
    onSaved?: () => void;
    onCancel?: () => void;
    onError?: () => void;
}

const DeliveryEdit = ({ deliveryId, isDelivered = false, onSaved, onCancel, onError }: Props) => {
    const { showLoading, hideLoading } = useContext(AppContext);
    const { navigationPage } = useNavigationPage();

    const { editDelivery, updateDelivery, fetchingEditDelivery, fetchingUpdateDelivery } = useDeliveryService();
    const { getSocieties } = useSocietyService();
    const { getClients } = useClientService();
    const { doGet } = useFetch();

    // Estados
    const [delivery, setDelivery] = useState<DeliveryDataForm>(defaultDeliveryDataForm);
    const [errorFields, setErrorFields] = useState<any>();

    // Datos para los selects
    const [salesOrders, setSalesOrders] = useState<any[]>([]);
    const [locations, setLocations] = useState<Location[]>([]);
    const [societies, setSocieties] = useState<Society[]>([]);
    const [clients, setClients] = useState<Client[]>([]);

    useEffect(() => {
        loadInitialData();
        edit();
    }, [deliveryId]);

    const loadInitialData = () => {
        if (showLoading) showLoading('loading', 'Cargando datos...');

        // Cargar datos iniciales desde el endpoint CREATE_DELIVERY
        doGet({
            url: EndPoints.DELIVERIES.CREATE_DELIVERY,
            onSuccess: (response: ServiceResponse) => {
                setSalesOrders(response.data?.sales_orders || []);
                setLocations(response.data?.locations || []);
                if (hideLoading) hideLoading();
            },
            onError: (error) => {
                console.error('Error al cargar datos iniciales:', error);
                if (hideLoading) hideLoading();
            }
        });

        // Cargar sociedades
        getSocieties(
            {
                page: 1,
                per_page: 1000,
                sort: 'id',
                order: 'asc'
            },
            {
                onSuccess: (response: ServiceResponse) => {
                    setSocieties(response.data?.societies || []);
                },
                onError: (error) => {
                    console.error('Error al cargar sociedades:', error);
                }
            }
        );

        // Cargar clientes
        getClients(
            {
                page: 1,
                per_page: 1000,
                sort: 'id',
                order: 'asc',
                business_name: null
            },
            {
                onSuccess: (response: ServiceResponse) => {
                    setClients(response.data?.clients || []);
                },
                onError: (error) => {
                    console.error('Error al cargar clientes:', error);
                }
            }
        );
    };

    const edit = () => {
        editDelivery(deliveryId, {
            onSuccess: (response: ServiceResponse) => {
                const fetchedDelivery = response.data.delivery;
                setDelivery({
                    id: fetchedDelivery.id,
                    sales_order_id: fetchedDelivery.sales_order_id,
                    society_id: fetchedDelivery.society_id,
                    client_id: fetchedDelivery.client_id,
                    delivery_date: fetchedDelivery.delivery_date,
                    destination_id: fetchedDelivery.destination_id,
                    observations: fetchedDelivery.observations,
                    actual_delivery_date: fetchedDelivery.actual_delivery_date ?? undefined,
                    actual_delivery_registered_at: fetchedDelivery.actual_delivery_registered_at ?? undefined
                });
            },
            onError: (response: ServiceResponse) => {
                toast.error(response.message);
                if (onError) onError();
            }
        });
    };

    const update = () => {
        if (showLoading) showLoading('loading', 'Actualizando entrega...');

        updateDelivery(deliveryId, delivery, {
            onSuccess: (response: ServiceResponse) => {
                if (hideLoading) hideLoading();

                useReactConfirmAlert().successAlert({
                    title: 'Éxito',
                    message: response.message
                });

                toast.success(response.message, { autoClose: 2500 });
                setErrorFields(undefined);
                edit();
                if (onSaved) onSaved();
            },
            onError: (response: ServiceResponse) => {
                if (hideLoading) hideLoading();

                useReactConfirmAlert().errorAlert({
                    title: 'Error',
                    message: response.message
                });
            },
            onFieldError: (errorFields: ServiceResponse) => {
                if (hideLoading) hideLoading();
                setErrorFields(errorFields.data);
            }
        });
    };

    const cancel = () => {
        if (onCancel) {
            onCancel();
        } else {
            navigationPage('/deliveries');
        }
    };

    return (
        <>
            <div className="row">
                <div className="col-12">
                    <DeliveryForm
                        delivery={delivery}
                        setDelivery={setDelivery}
                        salesOrders={salesOrders}
                        locations={locations}
                        societies={societies}
                        clients={clients}
                        errorFields={errorFields}
                        is_edit={true}
                        is_locked={isDelivered}
                    />
                </div>
            </div>

            {!isDelivered && (
                <>
                    <hr />
                    <div className="row justify-content-end">
                        <div className="col-auto">
                            <ButtonCancelForm callbackCancel={cancel} locked={fetchingUpdateDelivery} />
                        </div>
                        <div className="col-auto">
                            <ButtonSaveForm callbackSave={update} locked={fetchingUpdateDelivery} />
                        </div>
                    </div>
                </>
            )}
        </>
    );
};

export default DeliveryEdit;


